import type { MessageId } from "@redotech/effect-api-shared/ids/definitions";
import type { Message } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import { useMemo } from "react";

export type FormattedChatMessage = Message;

export interface ChatMessageGroup {
  dateLabel: string;
  messages: Message[];
}

export interface ChatThreadViewState {
  messageGroups: ChatMessageGroup[];
  lastMessageId: MessageId | null;
}

export function useChatThreadView(messages: Message[]): ChatThreadViewState {
  const sortedMessages = useMemo(
    () => [...messages].sort((a, b) => a.sentAt.getTime() - b.sentAt.getTime()),
    [messages],
  );

  const lastMessageId = useMemo(() => {
    if (sortedMessages.length === 0) {
      return null;
    }
    return sortedMessages[sortedMessages.length - 1]?.id ?? null;
  }, [sortedMessages]);

  const messageGroups = useMemo(
    () => groupMessagesByDate(sortedMessages),
    [sortedMessages],
  );

  return { messageGroups, lastMessageId };
}

function groupMessagesByDate(messages: Message[]): ChatMessageGroup[] {
  const groups: ChatMessageGroup[] = [];
  let currentDateLabel: string | null = null;
  let currentMessages: Message[] = [];

  for (const message of messages) {
    const dateLabel = formatChatDate(message.sentAt);

    if (dateLabel !== currentDateLabel) {
      if (currentDateLabel !== null && currentMessages.length > 0) {
        groups.push({ dateLabel: currentDateLabel, messages: currentMessages });
      }
      currentDateLabel = dateLabel;
      currentMessages = [];
    }

    currentMessages.push(message);
  }

  if (currentDateLabel !== null && currentMessages.length > 0) {
    groups.push({ dateLabel: currentDateLabel, messages: currentMessages });
  }

  return groups;
}

function formatChatDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
